import { useEffect, useState } from "react"
import { motion } from "motion/react"
import { useRequest } from "../../../../hooks/useRequest"
import { userStore } from "../../../../store/userStore"
import { appStore } from "../../../../store/appStore"
import { User } from "../../../../types/entities/user"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"

type Initial = {
    lastName: string
    firstName: string
    patronymic: string
    email: string
    birthday: string
}

export default function Edit() {
    const { request, requestData } = useRequest()
    const { user, setUser, clearAuth } = userStore()
    const { city } = appStore()

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [status, setStatus] = useState<"idle" | "success" | "error">("idle")
    const [errorMsg, setErrorMsg] = useState("")

    const [lastName, setLastName] = useState("")
    const [firstName, setFirstName] = useState("")
    const [patronymic, setPatronymic] = useState("")
    const [phone, setPhone] = useState("")
    const [email, setEmail] = useState("")
    const [birthday, setBirthday] = useState("")

    const [initial, setInitial] = useState<Initial>({
        lastName: "",
        firstName: "",
        patronymic: "",
        email: "",
        birthday: "",
    })

    useEffect(() => {
        if (!user) return
        setLoading(true)
        request<User>("GET", `user?city_id=${city.id}`)
            .then(data => {
                setUser({ ...data, token: user.token })
                const init: Initial = {
                    lastName: data.last_name ?? "",
                    firstName: data.first_name ?? "",
                    patronymic: data.patronymic ?? "",
                    email: data.email ?? "",
                    birthday: data.birthday ?? "",
                }
                setInitial(init)
                setLastName(init.lastName)
                setFirstName(init.firstName)
                setPatronymic(init.patronymic)
                setPhone(data.phone ?? "")
                setEmail(init.email)
                setBirthday(init.birthday)
            })
            .finally(() => setLoading(false))
    }, [user?.id, city.id])

    const hasChanges =
        lastName.trim() !== initial.lastName ||
        firstName.trim() !== initial.firstName ||
        patronymic.trim() !== initial.patronymic ||
        email.trim() !== initial.email ||
        birthday !== initial.birthday

    const requiredFilled = lastName.trim() !== "" && firstName.trim() !== ""
    const canSave = hasChanges && requiredFilled

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!canSave || !user) return
        setSaving(true)
        setStatus("idle")
        setErrorMsg("")

        const fullName = [lastName.trim(), firstName.trim(), patronymic.trim()].filter(Boolean).join(" ")
        const phoneForApi = phone.startsWith("+") ? phone : `+7${phone.replace(/\D/g, "").slice(-10)}`

        const res = await requestData<User>({
            type: "PUT",
            path: "user/update",
            sendData: {
                full_name: fullName,
                phone: phoneForApi,
                email: email.trim(),
                birthday,
                notification_channel: 3,
                gender: 1,
                show_image: 1,
                notify: 1,
                city_id: city.id,
            },
            disableError: true,
        })
        setSaving(false)
        if (res.status === "success") {
            setUser({ ...user, ...res.data, token: user.token })
            setStatus("success")
            setInitial({
                lastName: lastName.trim(),
                firstName: firstName.trim(),
                patronymic: patronymic.trim(),
                email: email.trim(),
                birthday,
            })
        } else {
            setStatus("error")
            setErrorMsg(res.error?.message ?? "Не удалось сохранить")
        }
    }

    const handleDeleteAccount = async () => {
        const confirmed = window.confirm("Уверены, что хотите удалить аккаунт? Это действие необратимо.")
        if (!confirmed) return
        await request("DELETE", "user")
        clearAuth()
    }

    const inputClass =
        "w-full px-4 py-3.5 rounded-[14px] border border-white/10 bg-white/[0.06] text-white text-[15px] outline-none focus:border-[#e4206b]/50 focus:bg-white/[0.09] transition-all placeholder:text-white/20"
    const disabledInputClass =
        "w-full px-4 py-3.5 rounded-[14px] border border-white/10 bg-white/[0.03] text-white/40 text-[15px] outline-none cursor-not-allowed"
    const labelClass = "text-white/60 text-[12px] uppercase tracking-[0.08em]"
    const cardClass = "rounded-[22px] border border-white/10 bg-white/[0.05] p-6 flex flex-col gap-3"

    if (loading) {
        return (
            <div className="flex justify-center py-16">
                <motion.div
                    className="size-10 rounded-full border-2 border-white/10 border-t-[#349be6]"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 0.8, ease: "linear", repeat: Infinity }}
                />
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <h2
                className="text-white"
                style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 24, letterSpacing: "-0.01em" }}>
                ✏️ Редактирование
            </h2>

            <motion.form
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                onSubmit={handleSave}
                className="flex flex-col gap-4">

                {/* ФИО */}
                <div className={cardClass}>
                    <label className={labelClass} style={{ fontFamily: MONO, fontWeight: 700 }}>
                        ФИО
                    </label>
                    <input
                        type="text"
                        value={lastName}
                        onChange={e => { setLastName(e.target.value); setStatus("idle") }}
                        placeholder="Фамилия"
                        className={inputClass}
                        style={{ fontFamily: BODY }}
                        required
                    />
                    <input
                        type="text"
                        value={firstName}
                        onChange={e => { setFirstName(e.target.value); setStatus("idle") }}
                        placeholder="Имя"
                        className={inputClass}
                        style={{ fontFamily: BODY }}
                        required
                    />
                    <input
                        type="text"
                        value={patronymic}
                        onChange={e => { setPatronymic(e.target.value); setStatus("idle") }}
                        placeholder="Не указано"
                        className={inputClass}
                        style={{ fontFamily: BODY }}
                    />
                    <p
                        className="text-white/30 text-[11px]"
                        style={{ fontFamily: BODY }}>
                        Фамилия и имя — обязательные поля. Отчество необязательно.
                    </p>
                </div>

                {/* Телефон */}
                <div className={cardClass}>
                    <label className={labelClass} style={{ fontFamily: MONO, fontWeight: 700 }}>
                        Телефон
                    </label>
                    <input
                        type="tel"
                        value={phone}
                        disabled
                        readOnly
                        className={disabledInputClass}
                        style={{ fontFamily: BODY }}
                    />
                    <p
                        className="text-white/30 text-[11px]"
                        style={{ fontFamily: BODY }}>
                        Для изменения номера телефона обратитесь в поддержку.
                    </p>
                </div>

                {/* Email */}
                <div className={cardClass}>
                    <label className={labelClass} style={{ fontFamily: MONO, fontWeight: 700 }}>
                        Email
                    </label>
                    <input
                        type="email"
                        value={email}
                        onChange={e => { setEmail(e.target.value); setStatus("idle") }}
                        placeholder="your@email.com"
                        className={inputClass}
                        style={{ fontFamily: BODY }}
                    />
                </div>

                {/* Дата рождения */}
                <div className={cardClass}>
                    <label className={labelClass} style={{ fontFamily: MONO, fontWeight: 700 }}>
                        Дата рождения
                    </label>
                    <input
                        type="date"
                        value={birthday}
                        onChange={e => { setBirthday(e.target.value); setStatus("idle") }}
                        className={inputClass}
                        style={{ fontFamily: BODY, colorScheme: "dark" }}
                    />
                </div>

                {/* Статусные сообщения */}
                {status === "success" && (
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-[#29cc6a] text-[13px] text-center"
                        style={{ fontFamily: BODY }}>
                        ✓ Данные успешно сохранены
                    </motion.p>
                )}
                {status === "error" && (
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-[#e4206b] text-[13px] text-center"
                        style={{ fontFamily: BODY }}>
                        {errorMsg || "Не удалось сохранить"}
                    </motion.p>
                )}

                <motion.button
                    whileHover={canSave ? { scale: 1.02 } : {}}
                    whileTap={canSave ? { scale: 0.97 } : {}}
                    type="submit"
                    disabled={!canSave || saving}
                    className="w-full rounded-[14px] py-4 text-white text-[15px] transition-all"
                    style={{
                        background: canSave ? "#e4206b" : "rgba(255,255,255,0.08)",
                        fontFamily: MONO,
                        fontWeight: 700,
                        opacity: canSave ? 1 : 0.45,
                        boxShadow: canSave ? "0 0 18px rgba(228,32,107,0.4)" : "none",
                    }}>
                    {saving ? "Сохранение..." : "Сохранить"}
                </motion.button>
            </motion.form>

            {/* Удалить аккаунт */}
            <div className="flex justify-center pt-2 pb-4">
                <button
                    type="button"
                    onClick={handleDeleteAccount}
                    className="text-[15px] transition-opacity hover:opacity-70 active:opacity-50"
                    style={{ fontFamily: BODY, fontWeight: 400, color: "#FF6359" }}>
                    Удалить аккаунт
                </button>
            </div>
        </div>
    )
}
